import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import TextareaAutosize from 'react-textarea-autosize';
import classNames from 'classnames';
import Checkbox from 'rc-checkbox';
import { FooterButtons } from '../../../../shared/components/Modals/FooterButtons';
import { ConfirmationModal } from '../../../modals/ContentModals/ConfirmationModal';
import ResultModal from '../../../../shared/components/Modals/MessageModal';
import CustomSelectBox from './CustomSelectWithDefaultSelectComponent';
import { BROADCAST_TYPES } from '../../../../constants/constants';
import { getAllFacilities } from '../../../../redux/actions/staffActions';
import {
  handleRequest,
  handleUploadRequest,
} from '../../../../redux/services/APIService';
import { cacheKeys } from '../../../../hooks/broadcasts/cacheKeys';
import Error from '../../../../shared/components/form/Error';

const pdfFileIcon = `${process.env.PUBLIC_URL}/img/pdf-file-icon.png`;
const MAX_MESSAGE_LENGTH = 10000;
const MAX_IMAGES = 5;
const MAX_PDFS = 5;

const RemoveIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 2L2 10M10 10L2 2"
      stroke="#17191A"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const Spinner = () => (
  <svg
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <circle cx="12" cy="2.5" r="1.5" opacity=".14" />
      <circle cx="16.75" cy="3.77" r="1.5" opacity=".29" />
      <circle cx="20.23" cy="7.25" r="1.5" opacity=".43" />
      <circle cx="21.50" cy="12.00" r="1.5" opacity=".57" />
      <circle cx="20.23" cy="16.75" r="1.5" opacity=".71" />
      <circle cx="16.75" cy="20.23" r="1.5" opacity=".86" />
      <circle cx="12" cy="21.5" r="1.5" />
      <animateTransform
        attributeName="transform"
        type="rotate"
        calcMode="discrete"
        dur="0.75s"
        values="0 12 12;30 12 12;60 12 12;90 12 12;120 12 12;150 12 12;180 12 12;210 12 12;240 12 12;270 12 12;300 12 12;330 12 12;360 12 12"
        repeatCount="indefinite"
      />
    </g>
  </svg>
);

const FACILITY_BROADCAST_AUDIENCE_IDS = [
  'all_registered_parents',
  'parents_canine_type',
  'parents_feline_type',
];

const SUPERADMIN_BROADCAST_AUDIENCE_IDS = [
  'all_registered_parents',
  'parents_canine_type',
  'parents_feline_type',
];

export function CreateBroadcastModal({
  isOpen,
  setIsOpen,
  isSuperAdmin = false,
  onSuccess,
}) {
  const dispatch = useDispatch();

const audienceOptions = useMemo(() => {
  const allowedIds = isSuperAdmin
    ? SUPERADMIN_BROADCAST_AUDIENCE_IDS
    : FACILITY_BROADCAST_AUDIENCE_IDS;
  return BROADCAST_TYPES.filter((t) => allowedIds.includes(t.id));
}, [isSuperAdmin]);
  const queryClient = useQueryClient();
  const facilityAll = useSelector((state) => state.staff?.facilityAll || []);

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [audience, setAudience] = useState([]);
  const [facility, setFacility] = useState([]);
  const [attachments, setAttachments] = useState({ images: [], pdfs: [] });
  const [sendPush, setSendPush] = useState(false);
  const [fileUploadErrors, setFileUploadErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [textAboveButtons, setTextAboveButtons] = useState('');

  const [errors, setErrors] = useState({});
  const [responseState, setResponseState] = useState({
    show: false,
    type: '',
    message: '',
  });

  const fileInputRef = useRef(null);
  const isMessageTooLong = content.length > MAX_MESSAGE_LENGTH;

  useEffect(() => {
    if (isOpen && isSuperAdmin) {
      dispatch(getAllFacilities());
    }
  }, [isOpen, isSuperAdmin, dispatch]);

  const validate = () => {
    const newErrors = {};

    if (!subject || subject.trim() === '') {
      newErrors.subject = 'Subject cannot be empty';
    }
    if (!content || content.trim() === '') {
      newErrors.content = 'Message cannot be empty';
    }
    if (audience.length === 0) {
      newErrors.audience = 'Audience cannot be empty';
    }
    if (isSuperAdmin && facility.length === 0) {
      newErrors.facility = 'Facility cannot be empty';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReset = () => {
    setSubject('');
    setContent('');
    setAudience([]);
    setFacility([]);
    setAttachments({ images: [], pdfs: [] });
    setSendPush(false);
    setFileUploadErrors([]);
    setTextAboveButtons('');
    setErrors({});
  };

  const handleClose = () => {
    setIsConfirmModalOpen(false);
    setIsOpen(false);
    handleReset();
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const validImageTypes = ['image/jpeg', 'image/png'];
    const validPdfTypes = ['application/pdf'];

    setTextAboveButtons('');
    const newAttachments = { images: [], pdfs: [] };
    const errorSet = new Set();

    files.forEach((file) => {
      if (validImageTypes.includes(file.type)) {
        const total = attachments.images.length + newAttachments.images.length;
        if (total >= MAX_IMAGES) {
          errorSet.add(`You can upload maximum ${MAX_IMAGES} images`);
          return;
        }
        newAttachments.images.push(file);
        return;
      }

      if (validPdfTypes.includes(file.type)) {
        const total = attachments.pdfs.length + newAttachments.pdfs.length;
        if (total >= MAX_PDFS) {
          errorSet.add(`You can upload maximum ${MAX_PDFS} PDFs`);
          return;
        }
        newAttachments.pdfs.push(file);
        return;
      }

      errorSet.add(
        'Invalid file type. Only images (JPEG, PNG) or PDFs allowed.',
      );
    });

    setFileUploadErrors(Array.from(errorSet));

    if (newAttachments.images.length || newAttachments.pdfs.length) {
      setAttachments((prev) => ({
        images: [...prev.images, ...newAttachments.images],
        pdfs: [...prev.pdfs, ...newAttachments.pdfs],
      }));
    }

    event.target.value = null;
  };

  const removeAttachment = (type, index) => {
    setAttachments((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
    setFileUploadErrors([]);
  };

  const handleFilter = (name, list) => {
    if (name === 'audience') {
      setAudience(list);
      setErrors((prev) => ({ ...prev, audience: null }));
    } else if (name === 'facility') {
      setFacility(list);
      setErrors((prev) => ({ ...prev, facility: null }));
    }
  };

  const handleSend = async () => {
  if (!validate()) return;

  const audienceNames = audience
    .map((id) => BROADCAST_TYPES.find((t) => t.id === id)?.name || id)
    .join(', ');

  const facilityNames = isSuperAdmin
    ? facility
        .map(
          (id) =>
            facilityAll.find((f) => f.id === id)?.displayName || id,
        )
        .join(', ')
    : '';

  const imageCount = attachments.images.length;
  const pdfCount = attachments.pdfs.length;

  let summary = `Audience: ${audienceNames || '(none)'}\n`;
  if (isSuperAdmin) {
    summary += `Facility: ${facilityNames || '(none)'}\n`;
  }
  summary += `Images: ${imageCount}\n`;
  summary += `PDFs: ${pdfCount}\n`;
  if (isSuperAdmin) {
    summary += `Send push: ${sendPush ? 'Yes' : 'No'}\n`;
  }
  summary += '\nSend this Broadcast?';

  if (isSuperAdmin && !window.confirm(summary)) return;

  setIsLoading(true);

    try {
      const uploadedImages = [];
      const uploadedDocs = [];

      // Upload images to Cloudinary
      for (const image of attachments.images) {
        const result = await handleUploadRequest(image, 'newsfeed');
        uploadedImages.push(result.data.secure_url);
      }

      // Upload PDFs to Cloudinary
      for (const pdf of attachments.pdfs) {
        const result = await handleUploadRequest(pdf, 'newsfeed');
        uploadedDocs.push(result.data.secure_url);
      }

      // Build image ratios (width / height for each uploaded image)
      const imageRatios = {};
      for (let i = 0; i < attachments.images.length; i++) {
        const file = attachments.images[i];
        const ratio = await getImageRatio(file);
        imageRatios[uploadedImages[i]] = ratio;
      }

      const payload = {
        subject,
        content,
        audience,
        images: uploadedImages,
        imageRatios,
        documents: uploadedDocs,
        sendPush: isSuperAdmin ? sendPush : true,
        ...(isSuperAdmin ? { facilities: facility } : {}),
      };

      await handleRequest('post', 'broadcast-message', true, payload);

      setResponseState({
        show: true,
        type: 'success',
        message: 'Broadcast sent successfully!',
      });

      setIsOpen(false);
      handleReset();

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });
        queryClient.invalidateQueries(cacheKeys.getBroadcasts());
        onSuccess?.();
      }, 2000);
    } catch (error) {
      console.error(error);
      setResponseState({
        show: true,
        type: 'danger',
        message: 'Failed to send broadcast.',
      });

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const hasUnsavedChanges =
    subject || content || attachments.images.length > 0 || attachments.pdfs.length > 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="md"
        className="nested-modal message-with-attachments-modal model-form centered"
      >
        <ModalHeader>
          <h3>Create Broadcast</h3>
        </ModalHeader>
        <ModalBody>
          <div className="message-container">
            {/* Subject */}
            <FormGroup>
              <Label className="content-label">Subject</Label>
              <Input
                maxLength={200}
                name="subject"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  if (e.target.value) setErrors((prev) => ({ ...prev, subject: null }));
                }}
                type="text"
                placeholder="Enter subject..."
                disabled={isLoading}
              />
              {errors.subject && <Error text={errors.subject} />}
            </FormGroup>

            {/* Audience */}
            <FormGroup style={{ position: 'relative', zIndex: 2 }}>
              <Label>Select Audience</Label>
              <CustomSelectBox
                listName="audience"
                allItems={audienceOptions}
                isSelectAllRequired={false}
                placeHolderSingular="audience"
                placeHolderPlural="Audience"
                selectedItems={audience}
                disableValue="all_users"
                action={handleFilter}
              />
              {errors.audience && <Error text={errors.audience} />}
            </FormGroup>

            {/* Facility (super admin only) */}
            {isSuperAdmin && (
              <FormGroup style={{ position: 'relative', zIndex: 1 }}>
                <Label>Select Facility</Label>
                <CustomSelectBox
                  listName="facility"
                  allItems={facilityAll}
                  isSelectAllRequired={true}
                  placeHolderSingular="facility"
                  placeHolderPlural="Facilities"
                  selectedItems={facility}
                  action={handleFilter}
                />
                {errors.facility && <Error text={errors.facility} />}
              </FormGroup>
            )}

            {/* Message body */}
            <div className="textarea-container">
              <TextareaAutosize
                minRows={3}
                maxRows={8}
                style={{ marginBottom: isMessageTooLong ? '0' : '25px' }}
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  if (e.target.value) {
                    setTextAboveButtons('');
                    setErrors((prev) => ({ ...prev, content: null }));
                  }
                }}
                placeholder="Write a message..."
                disabled={isLoading}
              />

              {isMessageTooLong && (
                <div className="message-length-indicator text-danger">
                  <span className="indicator-error">
                    Text cannot exceed{' '}
                    {MAX_MESSAGE_LENGTH.toLocaleString('en-US')} characters.
                  </span>
                  <span className="indicator-count">
                    {content.length.toLocaleString('en-US')} /{' '}
                    {MAX_MESSAGE_LENGTH.toLocaleString('en-US')}
                  </span>
                </div>
              )}
            </div>
            {errors.content && <Error text={errors.content} />}

            {/* Attachment uploader */}
            <div className="attachment-uploader">
              <input
                disabled={isLoading}
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept="image/jpeg,image/png,application/pdf"
                multiple
              />
              <label
                className={classNames(
                  'add-attachment-button nav-link-style',
                  isLoading && 'disabled',
                )}
                onClick={() => fileInputRef.current.click()}
              >
                + Add attachment
              </label>
            </div>

            {/* Attachments list */}
            <ul
              className={classNames(
                'attachments-list',
                (attachments.images.length > 0 ||
                  attachments.pdfs.length > 0) &&
                  'visible',
              )}
            >
              {attachments.images.map((file, index) => (
                <li key={`img-${index}`} className="attachment-item">
                  <div className="attachment-preview">
                    <img
                      src={
                        typeof file === 'string'
                          ? file
                          : URL.createObjectURL(file)
                      }
                      alt={file.name}
                    />
                  </div>
                  <button
                    disabled={isLoading}
                    className="remove-attachment-button"
                    onClick={() => removeAttachment('images', index)}
                  >
                    <RemoveIcon />
                  </button>
                </li>
              ))}
              {attachments.pdfs.map((file, index) => (
                <li key={`pdf-${index}`} className="attachment-item">
                  <div className="attachment-preview pdf-preview">
                    <img src={pdfFileIcon} alt="PDF" />
                  </div>
                  <p className="attachment-name" title={file.name}>
                    {file.name}
                  </p>
                  <button
                    disabled={isLoading}
                    className="remove-attachment-button"
                    onClick={() => removeAttachment('pdfs', index)}
                  >
                    <RemoveIcon />
                  </button>
                </li>
              ))}
            </ul>

            {/* File upload errors */}
            {fileUploadErrors.length > 0 && (
              <div className="upload-errors">
                {fileUploadErrors.map((error, index) => (
                  <p
                    key={index}
                    className="upload-attachments-error text-danger"
                  >
                    {error}
                  </p>
                ))}
              </div>
            )}

            {isSuperAdmin && (
              <label className="checkbox">
                <Checkbox
                  disabled={isLoading}
                  checked={sendPush}
                  onChange={(e) => setSendPush(e.target.checked)}
                />
                <span className="checkbox-label">Send Push Notification</span>
              </label>
            )}

            <div className="text-above-buttons">
              {textAboveButtons && !isLoading && (
                <p className="text-danger">{textAboveButtons}</p>
              )}
              {isLoading && (
                <div className="loading-message">
                  <Spinner />
                  <span>Sending broadcast...</span>
                </div>
              )}
            </div>
          </div>

          <FooterButtons
            onClose={() =>
              hasUnsavedChanges
                ? setIsConfirmModalOpen(true)
                : handleClose()
            }
            showSaveButton={true}
            onSave={handleSend}
            closeText="Cancel"
            saveText="Send Broadcast"
            saveDisabled={isLoading || isMessageTooLong}
            closeDisabled={isLoading}
          />
        </ModalBody>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleClose}
        onCancel={() => setIsConfirmModalOpen(false)}
      />

      <ResultModal
        show={responseState.show}
        type="alert"
        alertType={responseState.type}
        message={responseState.message}
        onClose={() => setResponseState({ show: false, type: '', message: '' })}
      />
    </>
  );
}

function getImageRatio(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = window.URL || window.webkitURL;
    img.onload = function () {
      resolve(this.width / this.height);
    };
    img.onerror = () => resolve(1);
    img.src = url.createObjectURL(file);
  });
}
